import * as React from "react";
import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type NumberFieldProps = BaseNumberField.Root.Props & {
  label: string;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: string;
};

export default function NumberField({
  id: idProp,
  label,
  error,
  helperText,
  size = "medium",
  ...rootProps
}: NumberFieldProps) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const helperTextId = `${id}-helper-text`;

  return (
    <BaseNumberField.Root
      {...rootProps}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
          fullWidth
        >
          {props.children}
        </FormControl>
      )}
    >
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            aria-describedby={helperTextId}
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onFocus={props.onFocus}
            slotProps={{ input: props }}
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  flexDirection: "column",
                  maxHeight: "unset",
                  alignSelf: "stretch",
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  ml: 0,
                  "& button": {
                    py: 0,
                    flex: 1,
                    borderRadius: 0.5,
                  },
                }}
              >
                <BaseNumberField.Increment
                  render={
                    <IconButton size={size} aria-label={`Increase ${label}`} />
                  }
                >
                  <KeyboardArrowUpIcon
                    fontSize={size}
                    sx={{ transform: "translateY(2px)" }}
                  />
                </BaseNumberField.Increment>
                <BaseNumberField.Decrement
                  render={
                    <IconButton size={size} aria-label={`Decrease ${label}`} />
                  }
                >
                  <KeyboardArrowDownIcon
                    fontSize={size}
                    sx={{ transform: "translateY(-2px)" }}
                  />
                </BaseNumberField.Decrement>
              </InputAdornment>
            }
            sx={{ pr: 0 }}
          />
        )}
      />
      <FormHelperText id={helperTextId} sx={{ ml: 0, "&:empty": { mt: 0 } }}>
        {helperText}
      </FormHelperText>
    </BaseNumberField.Root>
  );
}
